import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Draft {
  id: string;
  event_id: string;
  content: any;
  original_content?: any;
  ai_provider: string;
  ai_model: string;
  confidence_score: number;
  quality_score: number;
  status: string;
  source: string;
  event_type: string;
  event_payload: any;
  reviewer_name?: string;
  reviewed_at?: string;
  created_at: string;
}

export const HumanTasterInbox: React.FC = () => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    fetchDrafts();
  }, [filter]);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const status = filter === 'all' ? '' : `?status=${filter}`;
      const response = await fetch(`/api/ahtl/drafts${status}`);
      const data = await response.json();
      setDrafts(data.drafts || []);
    } catch (error) {
      console.error('Failed to fetch drafts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (draftId: string) => {
    try {
      const response = await fetch(`/api/ahtl/drafts/${draftId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_notes: reviewNotes })
      });

      if (response.ok) {
        fetchDrafts();
        setSelectedDraft(null);
        setReviewNotes('');
      }
    } catch (error) {
      console.error('Failed to approve draft:', error);
    }
  };

  const handleReject = async (draftId: string) => {
    try {
      const response = await fetch(`/api/ahtl/drafts/${draftId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_notes: reviewNotes })
      });

      if (response.ok) {
        fetchDrafts();
        setSelectedDraft(null);
        setReviewNotes('');
      }
    } catch (error) {
      console.error('Failed to reject draft:', error);
    }
  };

  const handleEdit = async (draftId: string) => {
    if (!editedContent) return;

    try {
      const response = await fetch(`/api/ahtl/drafts/${draftId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          content: editedContent,
          review_notes: reviewNotes 
        })
      });

      if (response.ok) {
        setEditMode(false);
        fetchDrafts();
        setSelectedDraft(null);
        setEditedContent(null);
        setReviewNotes('');
      }
    } catch (error) {
      console.error('Failed to update draft:', error);
    }
  };

  const getScoreBadgeColor = (score: number) => {
    if (score >= 0.9) return 'bg-green-100 text-green-800';
    if (score >= 0.7) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'edited': return 'bg-blue-100 text-blue-800';
      case 'published': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🍷 Human Taster Inbox
          </h1>
          <p className="text-gray-600">
            Review and approve AI-generated content before publication
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex space-x-2">
          {['pending', 'approved', 'rejected', 'all'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status as any)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Draft List */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Drafts</h2>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : drafts.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center text-gray-500">
                No drafts to review
              </div>
            ) : (
              <AnimatePresence>
                {drafts.map((draft) => (
                  <motion.div
                    key={draft.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`bg-white rounded-lg shadow-sm border p-4 cursor-pointer transition-all ${
                      selectedDraft?.id === draft.id ? 'ring-2 ring-blue-500' : 'hover:shadow-md'
                    }`}
                    onClick={() => {
                      setSelectedDraft(draft);
                      setEditedContent(draft.content);
                      setEditMode(false);
                      setReviewNotes('');
                    }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {draft.content.enhanced?.title || draft.event_type}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Source: {draft.source} • {new Date(draft.created_at).toLocaleString()}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(draft.status)}`}>
                        {draft.status}
                      </span>
                    </div>

                    <div className="flex space-x-4 mt-3">
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500">Confidence:</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getScoreBadgeColor(draft.confidence_score)}`}>
                          {(draft.confidence_score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500">Quality:</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getScoreBadgeColor(draft.quality_score)}`}>
                          {(draft.quality_score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500">AI:</span>
                        <span className="text-xs font-medium text-gray-700">
                          {draft.ai_model}
                        </span>
                      </div>
                    </div>

                    {draft.reviewer_name && (
                      <div className="mt-2 text-xs text-gray-500">
                        Reviewed by {draft.reviewer_name} at {new Date(draft.reviewed_at!).toLocaleString()}
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>

          {/* Draft Detail */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Draft Details</h2>
            
            {selectedDraft ? (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                {/* Draft Header */}
                <div className="mb-4 pb-4 border-b">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedDraft.content.enhanced?.title || selectedDraft.event_type}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(selectedDraft.status)}`}>
                      {selectedDraft.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-2">
                    <div>Source: <span className="font-medium">{selectedDraft.source}</span></div>
                    <div>Type: <span className="font-medium">{selectedDraft.event_type}</span></div>
                    <div>AI: <span className="font-medium">{selectedDraft.ai_provider}/{selectedDraft.ai_model}</span></div>
                    <div>Created: <span className="font-medium">{new Date(selectedDraft.created_at).toLocaleString()}</span></div>
                  </div>
                </div>

                {/* Scores */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-500 mb-1">Confidence Score</div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${selectedDraft.confidence_score * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold">
                        {(selectedDraft.confidence_score * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-500 mb-1">Quality Score</div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${selectedDraft.quality_score * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold">
                        {(selectedDraft.quality_score * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-gray-900">Content</h4>
                    {selectedDraft.status === 'pending' && (
                      <button
                        onClick={() => setEditMode(!editMode)}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        {editMode ? 'Cancel Edit' : 'Edit Content'}
                      </button>
                    )}
                  </div>
                  
                  {editMode ? (
                    <textarea
                      value={JSON.stringify(editedContent, null, 2)}
                      onChange={(e) => {
                        try {
                          setEditedContent(JSON.parse(e.target.value));
                        } catch {}
                      }}
                      className="w-full h-64 p-3 border rounded-lg font-mono text-sm"
                    />
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <pre className="text-sm whitespace-pre-wrap">
                        {JSON.stringify(selectedDraft.content, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>

                {/* Original Content (if edited) */}
                {selectedDraft.original_content && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Original AI Output</h4>
                    <div className="bg-yellow-50 rounded-lg p-4 max-h-32 overflow-y-auto">
                      <pre className="text-sm whitespace-pre-wrap">
                        {JSON.stringify(selectedDraft.original_content, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Review Notes */}
                {selectedDraft.status === 'pending' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Review Notes
                    </label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add notes about your review decision..."
                      className="w-full p-3 border rounded-lg"
                      rows={3}
                    />
                  </div>
                )}

                {/* Actions */}
                {selectedDraft.status === 'pending' && (
                  <div className="flex space-x-3">
                    {editMode ? (
                      <button
                        onClick={() => handleEdit(selectedDraft.id)}
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Save Changes
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleApprove(selectedDraft.id)}
                          className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          ✅ Approve
                        </button>
                        <button
                          onClick={() => handleReject(selectedDraft.id)}
                          className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium"
                        >
                          ❌ Reject
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
                Select a draft to view details
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};